import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import type { CommandView } from '../../../shared/model/commandView'
import { formatMoney, formatQuantity } from '../../pricing/model/format'
import { createKey, normalizeRid, sessionKey } from './keys'
import type { WarehouseChoiceState } from './warehouseChoiceSlice'
import { EMPTY_DELTA, hasChanges, mergeRows } from './rows'
import type {
  ChosenWarehouse,
  DeviationKind,
  InventoryIntentState,
  InventoryOperation,
  InventoryRowVm,
  InventorySession,
  InventoryVm,
  SaveBadge,
  ScanState,
  SessionDraft,
  SessionRow,
} from './types'
import { warehousePickOf, type StructureResult } from '../../structure/model/selectors'

export interface InventoryVmInput {
  resourceId: string
  /** Склад, выбранный в каждой базе (слайс `inventoryWarehouse`). */
  choice: WarehouseChoiceState
  intent: InventoryIntentState
  warehouses: StructureResult
  /** Сессия выбранного склада; `null` — склад не выбран (запроса нет). */
  session: QueryResultLike<OneCEnvelope<InventorySession | null>> | null
  cameraScanAvailable: boolean
}

const IDLE_QUERY: QueryView = { status: 'idle', refreshing: false, error: null }
const IDLE_SCAN: ScanState = { busy: false, error: null }
const EMPTY_ROWS: readonly SessionRow[] = []

const LABELS = { create: 'Создание товара', clear: 'Очистка сессии' } as const

/** Склад, выбранный в этой базе. */
export function selectChosenWarehouse(choice: WarehouseChoiceState, resourceId: string): ChosenWarehouse | undefined {
  return choice[normalizeRid(resourceId)]
}

export function selectSessionDraft(
  intent: InventoryIntentState,
  resourceId: string,
  warehouseRefKey: string,
): SessionDraft | undefined {
  return intent.drafts[sessionKey(resourceId, warehouseRefKey)]
}

function commandView(operation: InventoryOperation | undefined, label: string): CommandView | null {
  if (operation === undefined) return null
  return {
    label,
    status: operation.status,
    message: operation.error?.message ?? null,
    error: operation.error,
    resendable: operation.resendable,
  }
}

function deviationOf(row: SessionRow): { kind: DeviationKind; text: string } {
  if (!row.found || row.counted === null) return { kind: 'none', text: '—' }
  // Учёт не прочитан — отклонение от нуля, как у донора, но экран это называет.
  const deviation = row.counted - (row.stockQty ?? 0)
  if (deviation === 0) return { kind: 'match', text: 'сходится' }
  const amount = formatQuantity(Math.abs(deviation))
  return deviation > 0 ? { kind: 'surplus', text: `излишек +${amount}` } : { kind: 'shortage', text: `недостача −${amount}` }
}

function badgeOf(draft: SessionDraft | undefined): SaveBadge {
  if (draft === undefined) return 'idle'
  const { status, savedAt } = draft.save
  if (status === 'idle') return savedAt === null ? 'idle' : 'saved'
  return status
}

/**
 * vm окна «Инвентаризация 1С»: строки = сессия с сервера (кэш) + несохранённая
 * дельта (слайс). Мемоизация на экземпляр binding: два окна двух баз не
 * сбрасывают мемо друг друга.
 */
export function makeSelectInventoryVm() {
  const selectRows = createSelector(
    [
      (input: InventoryVmInput) => input.session?.data?.data?.rows ?? EMPTY_ROWS,
      (input: InventoryVmInput) => {
        const chosen = selectChosenWarehouse(input.choice, input.resourceId)
        return chosen === undefined ? undefined : selectSessionDraft(input.intent, input.resourceId, chosen.refKey)
      },
    ],
    (server, draft) => mergeRows(server, draft ?? EMPTY_DELTA),
  )

  return createSelector(
    [
      (input: InventoryVmInput) => input.resourceId,
      (input: InventoryVmInput) => selectChosenWarehouse(input.choice, input.resourceId),
      (input: InventoryVmInput) => input.intent,
      (input: InventoryVmInput) => input.warehouses,
      (input: InventoryVmInput) => input.session,
      (input: InventoryVmInput) => input.cameraScanAvailable,
      selectRows,
    ],
    (resourceId, chosen, intent, warehouses, session, cameraScanAvailable, merged): InventoryVm => {
      const rid = normalizeRid(resourceId)
      const selected = chosen?.refKey ?? ''
      const draft = chosen === undefined ? undefined : selectSessionDraft(intent, rid, selected)
      const key = chosen === undefined ? '' : sessionKey(rid, selected)
      const pick = warehousePickOf(warehouses)
      const sessionQuery = session === null ? IDLE_QUERY : queryView(session, () => false)
      const pendingDelta = draft !== undefined && hasChanges(draft)
      const saving = draft?.save.status === 'sending' || draft?.save.status === 'pending'
      const clear = chosen === undefined ? undefined : intent.clears[key]
      const clearBusy = clear?.status === 'sending' || clear?.status === 'unknown'
      // Перечитывание сессии (после сохранения) не закрывает скан: иначе сканер-клавиатура теряет фокус.
      // Пока идёт очистка, правок нет: иначе автосохранение вернуло бы очищенные строки.
      const ready = sessionQuery.status === 'ready' && !clearBusy
      const creates = Object.entries(intent.creates).filter(([id]) => id.startsWith(`${key}|`))
      const createBusy = creates.some(([, operation]) => operation.status === 'sending' || operation.status === 'unknown')

      const rows = merged.map((row): InventoryRowVm => {
        const createId = createKey(rid, selected, row.id)
        const create = intent.creates[createId]
        return {
          id: row.id,
          found: row.found,
          barcode: row.barcode,
          name: row.name?.trim() || '—',
          prices: row.prices.map((price) => ({
            warehouseRefKey: price.warehouseRefKey,
            warehouseName: price.warehouseName,
            text: price.price === null ? 'нет цены' : formatMoney(price.price),
            selected: price.warehouseRefKey === selected,
          })),
          stockText: row.stockQty === null ? 'не прочитан' : formatQuantity(row.stockQty),
          stockUnknown: row.found && row.stockQty === null,
          counted: row.counted,
          deviation: deviationOf(row),
          reprint: row.reprint,
          create: commandView(create, LABELS.create),
          createKey: create === undefined ? null : createId,
          editable: create === undefined || (create.status !== 'sending' && create.status !== 'unknown'),
        }
      })

      let surplus = 0
      let shortage = 0
      for (const row of merged) {
        if (!row.found || row.counted === null) continue
        const deviation = row.counted - (row.stockQty ?? 0)
        if (deviation > 0) surplus += deviation
        else shortage -= Math.min(deviation, 0)
      }

      let lockedReason: string | null = null
      if (pendingDelta || saving) lockedReason = 'Сначала дождитесь сохранения правок этого склада'
      else if (createBusy || clearBusy) lockedReason = 'Идёт команда 1С по этому складу — дождитесь её итога'

      // Склад сверяется с ответом 1С (RP-LSB2): до ответа — сохранённая подпись, после — имя из 1С; склада нет — выбор снят.
      const answered = pick.query.status === 'ready'
      const gone = chosen !== undefined && answered && !pick.warehouses.some((item) => item.refKey === chosen.refKey)
      const dropped = gone ? chosen.name : intent.droppedWarehouses[rid]

      return {
        resourceId: rid,
        warehouses: {
          query: pick.query,
          pick,
          selected: gone ? '' : selected,
          current: chosen !== undefined && !answered ? { value: chosen.refKey, label: chosen.name } : null,
          lockedReason,
          notice:
            dropped === undefined
              ? null
              : `Склада «${dropped}» в 1С больше нет — выбор склада снят. Выберите склад заново.`,
        },
        session: sessionQuery,
        ready,
        scan: chosen === undefined ? IDLE_SCAN : (intent.scans[key] ?? IDLE_SCAN),
        rows,
        totals: {
          rows: merged.length,
          surplus: formatQuantity(surplus),
          shortage: formatQuantity(shortage),
          reprints: merged.filter((row) => row.reprint && row.found).length,
        },
        save: { badge: badgeOf(draft), error: draft?.save.error ?? null, savedAt: draft?.save.savedAt ?? null },
        clear: commandView(clear, LABELS.clear),
        clearKey: clear === undefined ? null : key,
        canClear: ready && merged.length > 0 && !pendingDelta && !saving && !clearBusy && !createBusy,
        createWarehouses: pick.warehouses.map((item) => ({
          refKey: item.refKey,
          name: item.name,
          hasRetailPriceType: item.hasRetailPriceType,
        })),
        unsaved: pendingDelta || saving || createBusy || clearBusy,
        cameraScanAvailable,
      }
    },
  )
}

/**
 * Отмеченные «перепечатать» строки выбранного склада этой базы — для подбора
 * ценников: склад и позиции с ценой ЭТОГО склада. `null` — склад не выбран.
 */
export function selectInventoryReprints(input: Pick<InventoryVmInput, 'resourceId' | 'choice' | 'intent' | 'session'>): {
  warehouse: ChosenWarehouse
  items: { productRefKey: string; name: string; barcode: string; price: number | null }[]
} | null {
  const chosen = selectChosenWarehouse(input.choice, input.resourceId)
  if (chosen === undefined) return null
  const draft = selectSessionDraft(input.intent, input.resourceId, chosen.refKey)
  const rows = mergeRows(input.session?.data?.data?.rows ?? EMPTY_ROWS, draft ?? EMPTY_DELTA)
  return { warehouse: chosen, items: reprintItems(rows, chosen.refKey) }
}

/** Отмеченные «перепечатать» строки склада — позиции подбора ценников. */
export function reprintItems(
  rows: readonly SessionRow[],
  warehouseRefKey: string,
): { productRefKey: string; name: string; barcode: string; price: number | null }[] {
  return rows
    .filter((row) => row.reprint && row.found && row.productRefKey !== null)
    .map((row) => ({
      productRefKey: row.productRefKey ?? '',
      name: row.name ?? '',
      barcode: row.barcode,
      price: row.prices.find((price) => price.warehouseRefKey === warehouseRefKey)?.price ?? null,
    }))
}

export { mergeRows }
